"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WINANSI_EXTRA_CODE_POINTS = exports.OVERLAY_NUMERIC_PATTERN = exports.OVERLAY_EMAIL_PATTERN = void 0;
exports.isWinAnsiOverlayText = isWinAnsiOverlayText;
exports.normalizeOverlayText = normalizeOverlayText;
exports.overlayTextIssue = overlayTextIssue;
/**
 * What Cortex `set_data` accepts for a free-text overlay, mirrored so a client never shows a value
 * as valid that the server then refuses. The source of truth is the eConsent package's
 * `ec__econsent_data_lib` (`validateRequestedDataValue`); change both together.
 *
 * `templates.consent.framework.components.text_input~1_0` keeps its own copy because a template
 * cannot import a workspace lib; both specs pin the same table of values so the copies cannot drift.
 */
exports.OVERLAY_EMAIL_PATTERN = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/** A finite decimal `ec__numeric` can store: no exponent, no leading `.`, no trailing `.`. */
exports.OVERLAY_NUMERIC_PATTERN = /^-?\d+(\.\d+)?$/;
/**
 * Code points outside ASCII and Latin-1 that WinAnsi (the encoding of the archive's Helvetica)
 * can still draw. Anything else cannot be stamped onto the archived PDF.
 */
exports.WINANSI_EXTRA_CODE_POINTS = [
    0x0152, 0x0153, 0x0160, 0x0161, 0x0178, 0x017d, 0x017e, 0x0192, 0x02c6, 0x02dc, 0x2013, 0x2014, 0x2018, 0x2019,
    0x201a, 0x201c, 0x201d, 0x201e, 0x2020, 0x2021, 0x2022, 0x2026, 0x2030, 0x2039, 0x203a, 0x20ac, 0x2122,
];
const WINANSI_EXTRA = new Set(exports.WINANSI_EXTRA_CODE_POINTS);
/** True when every UTF-16 unit is WinAnsi-drawable. LF is allowed: the stamp draws it as a line break. */
function isWinAnsiOverlayText(value) {
    for (let i = 0; i < value.length; i += 1) {
        const cp = value.charCodeAt(i);
        if (cp === 0x0a || (cp >= 0x20 && cp <= 0x7e) || (cp >= 0xa0 && cp <= 0xff) || WINANSI_EXTRA.has(cp)) {
            continue;
        }
        return false;
    }
    return true;
}
/** `v` (already matching {@link OVERLAY_NUMERIC_PATTERN}) without the zeros a number drops: leading, trailing, and `-0`. */
function stripNumericZeros(v) {
    const [whole, fraction = ''] = v.replace(/^-/, '').split('.');
    const integer = whole.replace(/^0+(?=\d)/, '');
    const decimals = fraction.replace(/0+$/, '');
    const body = decimals ? `${integer}.${decimals}` : integer;
    return v.startsWith('-') && body !== '0' ? `-${body}` : body;
}
/**
 * True when `v` is a decimal Cortex stores without changing it beyond dropping zeros: a value that
 * would come back in exponent form (`0.0000001`) or with lost digits (`12345678901234567890`) is not.
 */
function isExactOverlayNumber(v) {
    if (!exports.OVERLAY_NUMERIC_PATTERN.test(v)) {
        return false;
    }
    const canonical = String(Number(v));
    return exports.OVERLAY_NUMERIC_PATTERN.test(canonical) && stripNumericZeros(v) === canonical;
}
/**
 * The exact string to send, as Cortex normalises it: CRLF and CR become LF, TAB becomes a space,
 * numeric/email are trimmed, and a valid number is its canonical `String(Number(v))` ("1.50" → "1.5").
 */
function normalizeOverlayText(type, value) {
    const unixLines = value.replace(/\r\n?/g, '\n').replace(/\t/g, ' ');
    if (type === 'ec__text') {
        return unixLines;
    }
    const trimmed = unixLines.trim();
    return type === 'ec__numeric' && isExactOverlayNumber(trimmed) ? String(Number(trimmed)) : trimmed;
}
/**
 * Why a value cannot be saved, or `undefined` when Cortex will accept it. `empty` is not a format
 * error — a cleared field is unanswered, and a previously saved value is deleted.
 * `value` must already be {@link normalizeOverlayText normalized}; that is what Cortex receives.
 */
function overlayTextIssue(type, value) {
    if (value.trim() === '') {
        return 'empty';
    }
    if (type === 'ec__numeric') {
        return isExactOverlayNumber(value) ? undefined : 'format';
    }
    if (type === 'ec__email' && !exports.OVERLAY_EMAIL_PATTERN.test(value)) {
        return 'format';
    }
    // The email pattern allows any Unicode before `@`, and the archive stamps emails too.
    return isWinAnsiOverlayText(value) ? undefined : 'encoding';
}
//# sourceMappingURL=overlay_text_validation.js.map